import {useState, useCallback, useMemo} from 'react';

import {accessGroupEnum, accessItemTypeEnum} from '../../../serverClientShared/sharedEnums';
import {GroupsRequester} from '../../Domain/Groups/GroupsRequester';
import {useStores} from '../../../Hooks';

const isSpecialAccessGroup = (source) => source === accessGroupEnum.WORKSPACE_PUBLIC || source === accessGroupEnum.GUEST;

const useAccessState = () => {
    const {procedureStore} = useStores();
    const [originalIsPublic, setOriginalIsPublic] = useState(null);
    const [editedIsPublic, setEditedIsPublic] = useState(null);
    const [originalAccessSources, setOriginalAccessSources] = useState([]);
    const [editedAccessSources, setEditedAccessSources] = useState([]);
    const [accessMembers, setAccessMembers] = useState([]);
    const [accessGroups, setAccessGroups] = useState([]);

    const initAccessState = useCallback((result) => {
        setOriginalIsPublic(result.isPublic);
        setEditedIsPublic(result.isPublic);
        setOriginalAccessSources(result.sources);
        setEditedAccessSources(result.sources);
        setAccessMembers(result.members || []);
        setAccessGroups(result.groups || []);
    }, []);

    const discardAccess = useCallback(() => {
        setEditedIsPublic(originalIsPublic);
        setEditedAccessSources(originalAccessSources);
    }, [originalIsPublic, originalAccessSources]);

    const saveAccessChanges = async (workspace, {itemId, itemType, nestedRoute}) => {
        const originalSet = new Set(originalAccessSources.filter((s) => !isSpecialAccessGroup(s)));
        const editedSet = new Set(editedAccessSources.filter((s) => !isSpecialAccessGroup(s)));
        const addedIds = [...editedSet].filter((id) => !originalSet.has(id));
        const removedIds = [...originalSet].filter((id) => !editedSet.has(id));
        const hasAccessChanges = addedIds.length || removedIds.length || editedIsPublic !== originalIsPublic;
        if (hasAccessChanges) {
            await GroupsRequester.saveItemAccess(workspace, {
                itemId,
                itemType: itemType || accessItemTypeEnum.ITEM,
                nestedRoute: nestedRoute || '',
                addedIds,
                removedIds,
                isPublic: editedIsPublic,
                wasPublic: originalIsPublic,
            });
            procedureStore.invalidateAccessSources(workspace, {itemId, nestedRoute});
        }
        setOriginalIsPublic(editedIsPublic);
        setOriginalAccessSources([...editedAccessSources]);
    };

    const isAccessDirty = useMemo(() => {
        if (editedIsPublic !== originalIsPublic) return true;
        if (editedAccessSources.length !== originalAccessSources.length) return true;
        const sortedEdited = [...editedAccessSources].sort();
        const sortedOriginal = [...originalAccessSources].sort();
        return sortedEdited.some((s, i) => s !== sortedOriginal[i]);
    }, [editedIsPublic, originalIsPublic, editedAccessSources, originalAccessSources]);

    return {
        originalIsPublic, editedIsPublic, setEditedIsPublic,
        originalAccessSources, editedAccessSources, setEditedAccessSources,
        accessMembers, setAccessMembers, accessGroups, setAccessGroups,
        initAccessState, discardAccess, saveAccessChanges, isAccessDirty,
    };
};

export default useAccessState;
